import {
  UploadStatus,
  UploadWidgetListItem,
} from '@aus-platform/design-system';
import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { FileType } from 'shared/api';
import type { MultiPartUploadUrls } from 'shared/hooks';
import { getFileUploadStatus, useMultipartUpload } from 'shared/hooks';
import {
  selectCompletedFilesCount,
  selectUploadWidget,
  setCompletedFilesCount,
  setFilesToUpload,
  setUploadStatusList,
} from 'shared/upload-widget-slices';
import type { UploadFileItem } from 'shared/upload-widget-slices/types';
import { UploadWidgetItemActions } from './upload-widget-item-actions';

export type UploadWidgetFilesProps = {
  uploadItem: UploadFileItem;
  isCancelledAllUploads: boolean;
  setIsCancelledAllUploads: React.Dispatch<React.SetStateAction<boolean>>;
  online: boolean;
};

export const UploadWidgetFiles: React.FC<UploadWidgetFilesProps> = ({
  uploadItem,
  isCancelledAllUploads,
  setIsCancelledAllUploads,
  online,
}) => {
  // Dispatcher.
  const dispatch = useAppDispatch();

  // Selectors.
  const { uploadStatusList, filesToUpload } =
    useAppSelector(selectUploadWidget);
  const completedFilesCount = useAppSelector(selectCompletedFilesCount);

  // Constants.
  const { id: fileId, file } = uploadItem;
  const currentFile = uploadStatusList.find(({ id }) => id === fileId)!;

  // States.
  const [areUrlsSet, setAreUrlsSet] = useState(false);
  const [speed, setSpeed] = useState<number>(0);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [uploadedBytes, setUploadedBytes] = useState<number>(
    currentFile?.sizes?.uploaded ?? 0,
  );
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>(
    UploadStatus.Pending,
  );
  const startTimeRef = useRef(Date.now());
  const [totalTimeTaken, setTotalTimeTaken] = useState<number>(0);

  const {
    progress,
    setUrls: setMultipartUploadUrls,
    startUploading,
    cancelUpload,
    pauseUpload,
    resumeUpload,
  } = useMultipartUpload({
    fileType: uploadItem.fileType ?? FileType.IterationFile,
  });

  // Constants.
  const multipartFileUploadStatus = getFileUploadStatus(progress);

  // useEffects.
  useEffect(() => {
    // When component mounts, we shouldn't create a new presigned url for already completed, progressing, or error files.
    if (!areUrlsSet && currentFile.uploadStatus === UploadStatus.Pending) {
      const multiPartUploadUrls: MultiPartUploadUrls = {
        startUploadUrl: `/files/${fileId}/upload-file/`,
        presignedUrl: `/files/${fileId}/presigned-url/`,
        completeUploadUrl: `/files/${fileId}/complete-upload/`,
      };

      setMultipartUploadUrls(multiPartUploadUrls);
      setAreUrlsSet(true);
    }
  }, []);

  useEffect(() => {
    let timer;
    if (areUrlsSet && currentFile.uploadStatus === UploadStatus.Pending) {
      timer = setTimeout(() => {
        startUploading(file);
      }, 1000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [areUrlsSet]);

  useEffect(() => {
    if (online) {
      if (currentFile.uploadStatus === UploadStatus.NetworkError) {
        handleResumeUpload();
      }
    } else {
      handleNetworkError();
    }
  }, [online]);

  // HACK: To cancel file upload.
  useEffect(() => {
    if (
      isCancelledAllUploads &&
      ![UploadStatus.Completed, UploadStatus.Cancelled].includes(
        currentFile.uploadStatus,
      )
    ) {
      dispatch(
        setUploadStatusList((uploadStatusList) =>
          uploadStatusList.map((item) =>
            item.uploadStatus !== UploadStatus.Completed
              ? {
                  ...item,
                  uploadStatus: UploadStatus.Cancelled,
                }
              : item,
          ),
        ),
      );
      setUploadStatus(UploadStatus.Cancelled);
      setIsCancelledAllUploads(false);
      if (
        ![UploadStatus.NetworkError, UploadStatus.Paused].includes(
          currentFile.uploadStatus,
        )
      ) {
        cancelUpload(true);
      }
    }
  }, [isCancelledAllUploads]);

  useEffect(() => {
    if (multipartFileUploadStatus.isFileIdle) {
      updateFileStatus(UploadStatus.Pending, 0, uploadedBytes);
      setUploadStatus(UploadStatus.Pending);
    } else if (multipartFileUploadStatus.isFileUploaded) {
      updateFileStatus(UploadStatus.Completed, progress, uploadedBytes);
      dispatch(setCompletedFilesCount(completedFilesCount + 1));
      setUploadStatus(UploadStatus.Completed);
    } else if (
      multipartFileUploadStatus.isFileUploading &&
      uploadStatus !== UploadStatus.Paused &&
      uploadStatus !== UploadStatus.NetworkError &&
      progress > 0
    ) {
      updateFileStatus(UploadStatus.InProgress, progress, uploadedBytes);
      setUploadStatus(UploadStatus.InProgress);
    } else if (multipartFileUploadStatus.isFileUploadError) {
      setUploadStatus(UploadStatus.Error);
      dispatch(
        setUploadStatusList((uploadStatusList) =>
          uploadStatusList.map((item) =>
            item.id === fileId && item.uploadStatus !== UploadStatus.Completed
              ? {
                  ...item,
                  uploadStatus: UploadStatus.Error,
                }
              : item,
          ),
        ),
      );
    } else if (multipartFileUploadStatus.isFileUploadCancelled) {
      setUploadStatus(UploadStatus.Cancelled);
      dispatch(
        setUploadStatusList((uploadStatusList) =>
          uploadStatusList.map((item) =>
            item.id === fileId && item.uploadStatus !== UploadStatus.Completed
              ? {
                  ...item,
                  uploadStatus: UploadStatus.Cancelled,
                }
              : item,
          ),
        ),
      );
    }

    if (progress > 0 && progress < 100) {
      const currentTime = Date.now();
      const timeElapsed = (currentTime - startTimeRef.current) / 1000;

      // Calculate upload speed.
      const uploadedBytes = (file.size * progress) / 100;
      const uploadSpeed = uploadedBytes / timeElapsed;
      setSpeed(parseFloat((uploadSpeed / 1024).toFixed(2)));
      setUploadedBytes((file.size * progress) / 100);

      // Calculate time left.
      const remainingBytes = file.size - uploadedBytes;
      const estimatedTimeLeft =
        uploadSpeed > 0 ? remainingBytes / uploadSpeed : 0;
      setTimeLeft(parseFloat(estimatedTimeLeft.toFixed(0)));
    } else if (progress >= 100) {
      const totalTimeTaken = (Date.now() - startTimeRef.current) / 1000;
      setTotalTimeTaken(totalTimeTaken);
    }
  }, [progress]);

  // Handlers.
  const handlePauseUpload = () => {
    pauseUpload();
    setUploadStatus(UploadStatus.Paused);
    dispatch(
      setUploadStatusList((uploadStatusList) =>
        uploadStatusList.map((item) =>
          item.id === fileId
            ? {
                ...item,
                uploadStatus: UploadStatus.Paused,
              }
            : item,
        ),
      ),
    );
  };

  const handleResumeUpload = () => {
    // Check if the current file is completed, error, or cancelled before resuming upload.
    if (
      [
        UploadStatus.Completed,
        UploadStatus.Error,
        UploadStatus.Cancelled,
      ].includes(currentFile.uploadStatus)
    ) {
      return;
    }

    // Check if network is available before resuming upload.
    if (!online) {
      setUploadStatus(UploadStatus.NetworkError);
      dispatch(
        setUploadStatusList((uploadStatusList) =>
          uploadStatusList.map((item) =>
            item.id === fileId
              ? {
                  ...item,
                  uploadStatus: UploadStatus.NetworkError,
                }
              : item,
          ),
        ),
      );
    } else {
      setUploadStatus(
        currentFile.progress! > 0
          ? UploadStatus.InProgress
          : UploadStatus.Pending,
      );
      resumeUpload();
      dispatch(
        setUploadStatusList((uploadStatusList) =>
          uploadStatusList.map((item) =>
            item.id === fileId
              ? {
                  ...item,
                  uploadStatus:
                    (item.progress ?? 0) > 0
                      ? UploadStatus.InProgress
                      : UploadStatus.Pending,
                }
              : item,
          ),
        ),
      );
    }
  };

  const handleNetworkError = () => {
    // Check if the current file is in progress or pending before setting network error status.
    if (
      [UploadStatus.InProgress, UploadStatus.Pending].includes(
        currentFile.uploadStatus,
      )
    ) {
      pauseUpload();
      setUploadStatus(UploadStatus.NetworkError);
      dispatch(
        setUploadStatusList((uploadStatusList) =>
          uploadStatusList.map((item) =>
            item.id === fileId
              ? {
                  ...item,
                  uploadStatus: UploadStatus.NetworkError,
                }
              : item,
          ),
        ),
      );
    }
  };

  const handleCancelUpload = () => {
    if (
      [
        UploadStatus.Completed,
        UploadStatus.Error,
        UploadStatus.Pending,
        UploadStatus.Cancelled,
      ].includes(currentFile.uploadStatus)
    ) {
      dispatch(
        setFilesToUpload(filesToUpload.filter(({ id }) => id !== fileId)),
      );
      dispatch(
        setUploadStatusList((uploadStatusList) =>
          uploadStatusList.filter((item) => item.id !== fileId),
        ),
      );
    } else if (
      [UploadStatus.NetworkError, UploadStatus.Paused].includes(
        currentFile.uploadStatus,
      )
    ) {
      setUploadStatus(UploadStatus.Cancelled);
      dispatch(
        setUploadStatusList((uploadStatusList) =>
          uploadStatusList.map((item) =>
            item.id === fileId
              ? {
                  ...item,
                  uploadStatus: UploadStatus.Cancelled,
                }
              : item,
          ),
        ),
      );
    } else {
      setUploadStatus(UploadStatus.Cancelled);
      cancelUpload(true);
    }

    if (currentFile.uploadStatus === UploadStatus.Pending) {
      cancelUpload(true);
    }
  };

  const updateFileStatus = (
    status: UploadStatus,
    progress: number,
    uploadedBytes: number,
  ) => {
    dispatch(
      setUploadStatusList((prevState) =>
        prevState.map((item) =>
          item.id === fileId
            ? {
                ...item,
                uploadStatus: status,
                progress,
                sizes: { uploaded: uploadedBytes, total: file.size },
                timeTaken: totalTimeTaken,
              }
            : item,
        ),
      ),
    );
  };

  return (
    <UploadWidgetListItem
      fileName={currentFile.fileName}
      uploadStatus={currentFile.uploadStatus}
      progress={currentFile.progress}
      speed={Number(speed)}
      timeLeft={
        currentFile.uploadStatus === UploadStatus.Completed
          ? currentFile?.timeTaken
          : Number(timeLeft)
      }
      sizes={{
        uploaded: currentFile.sizes?.uploaded || 0,
        total: currentFile.sizes?.total || file.size,
      }}
    >
      <UploadWidgetItemActions
        uploadStatus={currentFile.uploadStatus}
        fileDetails={uploadItem.fileDetails}
        onCancel={handleCancelUpload}
        onPause={handlePauseUpload}
        onResume={handleResumeUpload}
      />
    </UploadWidgetListItem>
  );
};
